import { Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { PasswordErrors, PasswordForm } from './use-password-form';
import { UserInformationForm } from './use-settings-form';

interface Props {
  firstName: string;
  lastName: string;
  language: string;
  email: string;
  isEmailInvalid: boolean;
  firstNameInvalid: boolean;
  lastNameInvalid: boolean;
  updateUserInfoLoading: boolean;

  oldPassword: string;
  newPassword: string;
  newPasswordRepeated: string;
  passwordInvalid: PasswordErrors | false;
  passwordLoading: boolean;

  onUserInfoChange(field: keyof UserInformationForm): (event: React.ChangeEvent<HTMLInputElement>) => void;

  saveUserInformationChanges(): void;

  onPasswordChange(field: keyof PasswordForm): (event: React.ChangeEvent<HTMLInputElement>) => void;

  savePasswordChanges(): void;
}

const ManageProfile: React.FC<Props> = ({
  firstName,
  lastName,
  language,
  email,
  isEmailInvalid,
  firstNameInvalid,
  lastNameInvalid,
  updateUserInfoLoading,
  oldPassword,
  newPassword,
  newPasswordRepeated,
  passwordInvalid,
  passwordLoading,
  onUserInfoChange,
  saveUserInformationChanges,
  onPasswordChange,
  savePasswordChanges,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const languageOptions = [
    { value: 'de', label: t('german') },
    { value: 'en', label: t('english') },
  ];

  return (
    <Stack className={classes.manageProfile} gap={2.5}>
      {/* Personal data */}
      <div className={classes.settingsBox}>
        <div className={classes.boxDescription}>
          <Typography className={classes.boxTitle}>{t('personalData')}</Typography>
          <Typography className={classes.boxHint}>{t('personalDataHint')}</Typography>
        </div>
        <div className={classes.boxFields}>
          <Stack gap={2.5}>
            <Stack direction={'row'} gap={2}>
              <PaiTextField
                label={t('firstName')}
                placeholder={t('enterFirstNameHere')}
                value={firstName}
                onChange={onUserInfoChange('firstName')}
                error={firstNameInvalid}
                errorText={firstNameInvalid ? t('firstNameLength') : undefined}
              />
              <PaiTextField
                label={t('lastName')}
                placeholder={t('enterLastNameHere')}
                value={lastName}
                onChange={onUserInfoChange('lastName')}
                error={lastNameInvalid}
                errorText={lastNameInvalid ? t('lastNameLength') : undefined}
              />
            </Stack>
            <PaiTextField
              label={t('email')}
              placeholder={t('enterEmailHere')}
              value={email}
              onChange={onUserInfoChange('email')}
              error={isEmailInvalid}
              errorText={isEmailInvalid ? t('noValidEmailError') : undefined}
            />
            <PaiSelect label={t('uiLanguage')} value={language} onChange={onUserInfoChange('language')} style={{ maxWidth: '320px' }}>
              {languageOptions.map((option) => (
                <PaiMenuItem key={option.value} value={option.value}>
                  {option.label}
                </PaiMenuItem>
              ))}
            </PaiSelect>
            <Stack className={classes.boxFooter} direction={'row'} justifyContent={'flex-end'}>
              <PaiButton
                text={t('save')}
                border={'rounded'}
                size={'small'}
                action={saveUserInformationChanges}
                disabled={isEmailInvalid || firstName.trim().length === 0 || lastName.trim().length === 0 || email.trim().length === 0}
                loading={updateUserInfoLoading}
              />
            </Stack>
          </Stack>
        </div>
      </div>

      {/* Change Password */}
      <div className={classes.settingsBox}>
        <div className={classes.boxDescription}>
          <Typography className={classes.boxTitle}>{t('changePassword')}</Typography>
          <Typography className={classes.boxHint}>{t('changePasswordHint')}</Typography>
        </div>
        <div className={classes.boxFields}>
          <Stack gap={2.5}>
            <PaiTextField
              label={t('currentPassword')}
              placeholder={t('enterCurrentPasswordHere')}
              value={oldPassword}
              type={'password'}
              onChange={onPasswordChange('oldPassword')}
              error={passwordInvalid === PasswordErrors.OLD_INVALID}
              errorText={passwordInvalid === PasswordErrors.OLD_INVALID ? t('wrongOldPassword') : undefined}
            />
            <PaiTextField
              label={t('newPassword')}
              placeholder={t('enterNewPasswordHere')}
              value={newPassword}
              type={'password'}
              onChange={onPasswordChange('newPassword')}
              error={passwordInvalid === PasswordErrors.NEW_TOO_SHORT || passwordInvalid === PasswordErrors.NEW_TOO_LONG}
              errorText={
                passwordInvalid === PasswordErrors.NEW_TOO_SHORT
                  ? t('passwordTooShort')
                  : passwordInvalid === PasswordErrors.NEW_TOO_LONG
                  ? t('passwordTooLong')
                  : undefined
              }
            />
            <PaiTextField
              label={t('confirmNewPassword')}
              placeholder={t('confirmNewPasswordHere')}
              value={newPasswordRepeated}
              type={'password'}
              onChange={onPasswordChange('newPasswordRepeated')}
              error={passwordInvalid === PasswordErrors.REPEATED_NO_MATCH}
              errorText={passwordInvalid === PasswordErrors.REPEATED_NO_MATCH ? t('passwordsNoMatch') : undefined}
            />
            <Stack className={classes.boxFooter} direction={'row'} justifyContent={'flex-end'}>
              <PaiButton
                text={t('save')}
                border={'rounded'}
                size={'small'}
                action={savePasswordChanges}
                disabled={
                  !!passwordInvalid || oldPassword.trim().length === 0 || newPassword.trim().length === 0 || newPasswordRepeated.trim().length === 0
                }
                loading={passwordLoading}
              />
            </Stack>
          </Stack>
        </div>
      </div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  manageProfile: {
    display: 'flex',
    flexDirection: 'column',
  },
  settingsBox: {
    background: '#FFF',
    borderRadius: '12px',
    padding: '28px 32px',
    display: 'flex',
    gap: '40px',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      gap: '20px',
      padding: '24px',
    },
  },
  boxDescription: {
    width: '240px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  boxTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 500,
  },
  boxHint: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    textWrap: 'pretty',
  },
  boxFields: {
    flex: 1,
    minWidth: 0,
    maxWidth: '640px',
    // Field styling per design: 13px grey label above a 44px field with a neutral border.
    '& .MuiTypography-root:first-of-type': {
      fontSize: theme.typography.pxToRem(13),
      color: theme.palette.greys.medium,
      marginLeft: 0,
      marginBottom: '6px',
    },
    '& .MuiTextField-root': {
      borderRadius: '8px',
    },
    // Keep the error border of the base component untouched, only restyle the resting state.
    '& .MuiTextField-root:has(.MuiInputBase-root:not(.Mui-error))': {
      border: `1px solid ${theme.palette.greys.lighter}`,
    },
    '& .MuiTextField-root:has(.MuiInputBase-root.Mui-focused:not(.Mui-error))': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiInputBase-input': {
      borderRadius: '8px',
      minHeight: '26px',
      padding: '8px 14px',
    },
    '& .MuiInputBase-input[type="password"]': {
      paddingRight: '40px',
    },
  },
  boxFooter: {
    paddingTop: '4px',
    // The save button keeps its own block – see report note about the shared save bar.
    '& .MuiButtonBase-root': {
      height: '40px',
      borderRadius: '8px',
      padding: '0 24px',
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 700,
    },
  },
}));

export default ManageProfile;
